// /pages/WipLotTatReportPage.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging, ColumnChooser, } from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import API_URL from '../config';

const WIP_TAT_API_URL = `${API_URL}/api/oracle/wip-lot-tat`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;

// ★★★ 기존에 정의한 Factory별 공정 순서를 그대로 재사용합니다. ★★★
const OPER_ORDER_BY_FACTORY = {
  'FT01': [
    'TB', 'IQC', 'DEREEL', 'BKE1', 'FTST', 'FTHT', 'FTCT', 'FTST2',
    'FSLT', 'FSLT2', 'QEI', 'LIS', 'BKE', 'QVI', 'TNR', 'RDC',
    'FPK', 'QPI', 'FGS', 'SHIP'
  ],
  'PT01': [
    'TB', 'IQC', 'PTST', 'BKE', 'PTST2', 'INK', 'BKE2', 'PTST3',
    'AW', 'PTST4', 'QEI', 'EQC', 'AC', 'EQC2', 'WPK', 'QPI',
    'FGS', 'SHIP'
  ],
};

// 데이터를 피벗팅하는 함수
const processPivotData = (rawData, factory) => {
  if (!rawData || rawData.length === 0) {
    return { pivotedData: [], dynamicColumns: [] };
  }
  const definedOrder = OPER_ORDER_BY_FACTORY[factory] || [];
  const groupedData = new Map();
  const operSet = new Set();

  rawData.forEach(row => {
    const key = row.LOT_ID;
    if (!groupedData.has(key)) {
      groupedData.set(key, { ...row, Opers: {} });
    }
    const entry = groupedData.get(key);
    if (row.OPER && row.QTY !== null) {
      // OPER와 OPER_CT 값을 모두 저장
      entry.Opers[row.OPER] = row.QTY;
      // OPER 이름에서 '_CT'를 제거하여 순수 공정 이름만 Set에 추가
      const pureOper = row.OPER.replace('_CT', '');
      operSet.add(pureOper);
    }
  });

  const dynamicColumns = Array.from(operSet).sort((a, b) => {
    const indexA = definedOrder.indexOf(a);
    const indexB = definedOrder.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b);
  });

  const pivotedData = Array.from(groupedData.values()).map(item => {
    const finalRow = { ...item };
    // dynamicColumns에 있는 모든 공정에 대해 OPER와 OPER_CT 값을 할당
    dynamicColumns.forEach(operName => {
      finalRow[operName] = item.Opers[operName] || null;
      finalRow[`${operName}_CT`] = item.Opers[`${operName}_CT`] || null;
    });
    delete finalRow.Opers;
    delete finalRow.OPER;
    delete finalRow.QTY;
    return finalRow;
  });

  return { pivotedData, dynamicColumns };
};


export default function WipLotTatReportPage({ user }) {
  const [data, setData] = useState([]);
  const [dynamicOperColumns, setDynamicOperColumns] = useState([]);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dataGridRef = useRef(null);


  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } })
      .then(res => setCustList(res.data.data || []))
      .catch(err => console.error("CUST 목록 조회 실패:", err));
  }, [selectedFactory]);

  useEffect(() => {
    if (!selectedFactory || !selectedCust) {
      setDeviceList([]);
      setSelectedDevice('ALL');
      return;
    }
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } })
      .then(res => {
        const fetchedDevices = res.data.data || [];
        const allOption = { CODE_CD: 'ALL' };
        setDeviceList([allOption, ...fetchedDevices]);
        setSelectedDevice('ALL');
      })
      .catch(err => console.error("DEVICE 목록 조회 실패:", err));
  }, [selectedFactory, selectedCust]);

  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);
  

  const fetchData = useCallback(() => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);
    setDynamicOperColumns([]);

    const params = {
      factory: selectedFactory,
      custCd: selectedCust,
      device: selectedDevice,
    };
    
    axios.get(WIP_TAT_API_URL, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        const { pivotedData, dynamicColumns } = processPivotData(res.data.data, selectedFactory);
        setData(pivotedData.map((row, idx) => ({ ...row, id: idx })));
        setDynamicOperColumns(dynamicColumns);
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message;
        setError(`조회 오류: ${errorMessage}`);
        notify(errorMessage, 'error', 3000);
      })
      .finally(() => setLoading(false));
  }, [selectedFactory, selectedCust, selectedDevice]);

  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>WIP Lot TAT Report</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <SelectBox items={['FT01', 'PT01']} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} label="FACTORY" width={150} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && <div className="loading-overlay"></div>}
        <DataGrid
          ref={dataGridRef}
          dataSource={data}
          keyExpr="id"
          // onExporting={onExporting}
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Search..." />
          <Sorting mode="multiple" />
          <ColumnChooser enabled={true} />

          {/* ★★★ [수정] 컬럼 정의 ★★★ */}
          <Column dataField="FACTORY" caption="Factory" width={100} fixed={true} />
          <Column dataField="CUSTOMER" caption="Customer" width={120} fixed={true} />
          <Column dataField="MAT_ID" caption="Device" width={150} fixed={true} />
          <Column dataField="LOT_ID" caption="Lot ID" width={150} />
          <Column dataField="LOT_TYPE" caption="Lot Type" width={100} />
          <Column dataField="HOLD_FLAG" caption="Hold" width={80} alignment="center" />
          <Column dataField="HOLD_CODE" caption="Hold Code" width={120} />
          <Column dataField="IN_QTY" caption="In Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="TOTAL_QTY" caption="Total Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="TOTAL_CT" caption="Total C/T" dataType="number" alignment="right" width={100} />
          
          {/* 동적 OPER 및 OPER_CT 컬럼 렌더링 */}
          {dynamicOperColumns.map(operName => (
            <React.Fragment key={operName}>
              <Column
                dataField={operName}
                caption={operName}
                dataType="number"
                alignment="right"
                width={90}
              />
              <Column
                dataField={`${operName}_CT`}
                caption={`${operName} C/T`}
                dataType="number"
                alignment="right"
                width={90}
              />
            </React.Fragment>
          ))}          
       
        </DataGrid>
      </div>
    </div>
  );
}